// 司机端接送机包车主页
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getOrderList, getOrderRecord, grabOrder, completeOrder, updateOrderStatus } from '../../api/airportTransfer';
import './index.scss';
import { Toast, ListView, PullToRefresh } from "antd-mobile";

// 抢单大厅组件
const OrderHall = ({ orders, loading, onRefresh, onAccept, pagination, onLoadMore }) => {
  // 创建ListView的数据源
  const dataSource = new ListView.DataSource({
    rowHasChanged: (r1, r2) => r1 !== r2,
  });
  
  // 渲染每一行数据
  const renderRow = (order) => (
    <div key={order.orderId} className="order-card">
      <div className="order-header">
        <span className="order-number"> {order.orderId}</span>
        <span className="order-status">
          {order.paymentStatus === 1 ? '已支付' : '未支付'}
        </span>
      </div>
      <div className="order-info">
        <div className="info-item">
          <span className="label">用车时间:</span>
          <span className="value">{order.departureTime}</span>
        </div>
        <div className="info-item">
          <span className="label">下单时间:</span>
          <span className="value">{order.estimateTime}</span>
        </div>
        {order.flightInfo && (
          <div className="info-item">
            <span className="label">航班信息:</span>
            <span className="value">{order.flightInfo}</span>
          </div>
        )}
        <div className="info-item">
          <span className="label">出发地:</span>
          <span className="value">{order.startName}</span>
        </div>
        <div className="info-item">
          <span className="label">目的地:</span>
          <span className="value">{order.destinationName}</span>
        </div>
        <div className="info-item">
          <span className="label">联系电话:</span>
          <span className="value">{order.passengerPhone}</span>
        </div>
        {order.passengerRemark && (
          <div className="info-item">
            <span className="label">乘客备注:</span>
            <span className="value">{order.passengerRemark}</span>
          </div>
        )}
        <div className="info-item">
          <span className="label">订单金额:</span>
          <span className="value price">¥{order.price}</span>
        </div>
      </div>
      <div className="order-actions">
        {order.paymentStatus === 1 && (
          <button 
            className="accept-btn" 
            onClick={() => onAccept(order.orderId)}
          >
            抢单
          </button>
        )}
      </div>
    </div>
  );

  // 渲染底部加载提示
  const renderFooter = () => (
    <>
      {loading ? (
        <div className="loading-more">加载更多数据中...</div>
      ) : (
        pagination.pageNo * pagination.pageSize >= pagination.total ? (
          <div className="loading-no-more">没有更多数据了</div>
        ) : null
      )}
    </>
  );

  const onEndReached = () => {
    // 如果还有更多数据可以加载
    if (!loading && pagination.pageNo * pagination.pageSize < pagination.total) {
      onLoadMore(pagination.pageNo + 1);
    }
  };

  return (
    <div className="order-list-container">
      {orders.length === 0 && !loading ? (
        <div className="empty-container">暂无可抢订单</div>
      ) : (
        <ListView
          dataSource={dataSource.cloneWithRows(orders)}
          renderRow={renderRow}
          renderFooter={renderFooter}
          style={{ height: 'calc(100vh - 0.82rem)', overflow: 'auto' }}
          pageSize={pagination.pageSize}
          onEndReached={onEndReached}
          onEndReachedThreshold={10}
          scrollRenderAheadDistance={500}
          initialListSize={pagination.pageSize}
          pullToRefresh={
            <PullToRefresh
              refreshing={loading}
              onRefresh={onRefresh}
              distanceToRefresh={25}
              indicator={{
                activate: '松开立即刷新',
                deactivate: '下拉可以刷新',
                release: '加载中...',
                finish: '刷新完成',
              }}
            />
          }
        />
      )}
    </div>
  );
};

// 接单记录组件
const OrderRecord = ({ orders, loading, onRefresh, onUpdateStatus, pagination, onLoadMore }) => {
  // 获取订单状态文本
  const getOrderStatusText = (status) => {
    const statusMap = {
      0: '已取消',
      1: '呼叫中',
      2: '接驾中',
      3: '司机到达上车点',
      4: '乘客上车',
      5: '已完成',
      6: '司机已收款',
    };
    return statusMap[status] || '未知状态';
  };
  
  // 获取按钮文本和下一状态
  const getActionButton = (status) => {
    switch (parseInt(status)) {
      case 2:
        return { text: '到达上车点', nextStatus: 3 };
      case 3:
        return { text: '乘客上车', nextStatus: 4 };
      case 4:
        return { text: '订单结束', nextStatus: 5 };
      default:
        return null;
    }
  };
  
  // 创建ListView的数据源
  const dataSource = new ListView.DataSource({
    rowHasChanged: (r1, r2) => r1 !== r2,
  });
  
  // 渲染每一行数据
  const renderRow = (order) => (
    <div key={order.orderId} className="order-card">
      <div className="order-header">
        <span className="order-number">{order.orderId}</span>
        <span className="order-status">{getOrderStatusText(order.orderStatus)}</span>
      </div>
      <div className="order-info">
        <div className="info-item">
          <span className="label">用车时间:</span>
          <span className="value">{order.departureTime}</span>
        </div>
        <div className="info-item">
          <span className="label">下单时间:</span>
          <span className="value">{order.estimateTime}</span>
        </div>
        {order.flightInfo && (
          <div className="info-item">
            <span className="label">航班信息:</span>
            <span className="value">{order.flightInfo}</span>
          </div>
        )}
        <div className="info-item">
          <span className="label">出发地:</span>
          <span className="value">{order.startName}</span>
        </div>
        <div className="info-item">
          <span className="label">目的地:</span>
          <span className="value">{order.destinationName}</span>
        </div>
        <div className="info-item">
          <span className="label">联系电话:</span>
          <span className="value">{order.passengerPhone}</span>
        </div>
        {order.passengerRemark && (
          <div className="info-item">
            <span className="label">乘客备注:</span>
            <span className="value">{order.passengerRemark}</span>
          </div>
        )}
        <div className="info-item">
          <span className="label">订单金额:</span>
          <span className="value price">¥{order.price}</span>
        </div>
        <div className="info-item">
          <span className="label">支付状态:</span>
          <span className="value">{order.paymentStatus === 1 ? '已支付' : '未支付'}</span>
        </div>
      </div>
      <div className="order-actions">
        <button 
          className="call-btn"
          onClick={() => window.location.href = `tel:${order.passengerPhone}`}
        >
          联系乘客
        </button>
        {getActionButton(order.orderStatus) && (
          <button 
            className="update-status-btn" 
            onClick={() => onUpdateStatus(order.orderId, getActionButton(order.orderStatus).nextStatus)}
          >
            {getActionButton(order.orderStatus).text}
          </button>
        )}
      </div>
    </div>
  );

  // 渲染底部加载提示
  const renderFooter = () => (
    <>
      {loading ? (
        <div className="loading-more">加载更多数据中...</div>
      ) : (
        pagination.pageNo * pagination.pageSize >= pagination.total ? (
          <div className="loading-no-more">没有更多数据了</div>
        ) : null
      )}
    </>
  );
  
  const onEndReached = () => {
    // 如果还有更多数据可以加载
    if (!loading && pagination.pageNo * pagination.pageSize < pagination.total) {
      onLoadMore(pagination.pageNo + 1);
    }
  };
  
  return (
    <div className="order-list-container">
      {orders.length === 0 && !loading ? (
        <div className="empty-container">暂无接单记录</div>
      ) : (
        <ListView
          dataSource={dataSource.cloneWithRows(orders)}
          renderRow={renderRow}
          renderFooter={renderFooter}
          style={{ height: 'calc(100vh - 0.82rem)', overflow: 'auto' }}
          pageSize={pagination.pageSize}
          onEndReached={onEndReached}
          onEndReachedThreshold={10}
          scrollRenderAheadDistance={500}
          initialListSize={pagination.pageSize}
          pullToRefresh={
            <PullToRefresh
              refreshing={loading}
              onRefresh={onRefresh}
              distanceToRefresh={25}
              indicator={{
                activate: '松开立即刷新',
                deactivate: '下拉可以刷新',
                release: '加载中...',
                finish: '刷新完成',
              }}
            />
          }
        />
      )}
    </div>
  );
};

// 加载更多提示组件
const LoadingMore = () => {
  return (
    <div className="loading-more-container">
      <div className="loading-spinner"></div>
      <span>加载更多...</span>
    </div>
  );
};

const AirportTransferList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const tabFromUrl = urlParams.get('tab') || 'hall';
  
  const [activeTab, setActiveTab] = useState(tabFromUrl); // 根据URL参数设置默认标签页
  const [orderHallData, setOrderHallData] = useState([]);
  const [orderRecordData, setOrderRecordData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageNo: 1,
    pageSize: 3,
    total: 0
  });

  // 获取抢单大厅数据
  const fetchOrderHallData = async (pageNo = 1, isLoadMore = false) => {
    setLoading(true);
    try {
      // 调用实际的API，查询抢单大厅数据
      const response = await getOrderList({
        orderStatus: 1, // 呼叫中的订单
        paymentStatus:1, // 已支付的订单
        pageNo,
        pageSize: pagination.pageSize,
        countFlag:true
      });
      console.info('获取抢单大厅数据', response);
      
      if (response && response.data && response.data.code === 200) {
        const newData = response.data.data.datas || [];
        newData.forEach(e=>{
            e.price = e.price ? (e.price / 100).toFixed(2) : '0.00';
        })
        
        // 如果是加载更多，则将新数据追加到现有数据后面
        if (isLoadMore) {
          setOrderHallData(prevData => [...prevData, ...newData]);
        } else {
          setOrderHallData(newData);
        }
        
        setPagination({
          ...pagination,
          pageNo: response.data.data.currentPage || pageNo,
          total: response.data.data.total || 0
        });
      } else if (response.data && response.data.errorMsg) {
        console.error(response.data.errorMsg);
        Toast.offline(response.data.errorMsg, 2);
      }
    } catch (error) {
      console.error('获取抢单大厅数据失败', error);
      Toast.offline('获取数据失败，请检查网络', 2);
    } finally {
      setLoading(false);
    }
  };
  
  // 获取接单记录数据
  const fetchOrderRecordData = async (pageNo = 1, isLoadMore = false) => {
    setLoading(true);
    try {
      // 调用实际的API，查询接单记录数据
      const response = await getOrderRecord({
        countFlag:true,
        pageNo,
        pageSize: pagination.pageSize
      });
      
      if (response && response.data && response.data.code === 200) {
        const newData = response.data.data.datas || [];
        newData.forEach(e=>{
            e.price = e.price ? (e.price / 100).toFixed(2) : '0.00';
        })
        
        // 如果是加载更多，则将新数据追加到现有数据后面
        if (isLoadMore) {
          setOrderRecordData(prevData => [...prevData, ...newData]);
        } else {
          setOrderRecordData(newData);
        }
        
        setPagination({
          ...pagination,
          pageNo: response.data.data.currentPage || pageNo,
          total: response.data.data.total || 0
        });
      } else if (response.data && response.data.errorMsg) {
        console.error(response.data.errorMsg);
        Toast.offline(response.data.errorMsg, 2);
      }
    } catch (error) {
      console.error('获取接单记录数据失败', error);
      Toast.offline('获取数据失败，请检查网络', 2);
    } finally {
      setLoading(false);
    }
  };

  // 抢单处理
  const handleGrabOrder = async (orderId) => {
    Toast.loading('抢单中...', 0, () => {}, true);
    try {
      const response = await grabOrder(orderId);
      Toast.hide();
      if (response && response.data && response.data.code === 200) {
        // 抢单成功，刷新数据
        Toast.success('抢单成功');
        fetchOrderHallData(1, false);
        fetchOrderRecordData(1, false);
      } else {
        console.error('抢单失败', response);
        Toast.fail('抢单失败: ' + (response?.data?.errorMsg || '未知错误'), 2);
      }
    } catch (error) {
      console.error('抢单出错', error);
      Toast.hide();
      Toast.fail('抢单出错，请稍后重试', 2);
    }
  };

  // 更新订单状态处理
  const handleUpdateOrderStatus = async (orderId, status) => {
    const statusTextMap = {
      3: '到达上车点',
      4: '乘客上车',
      5: '订单结束'
    };
    
    const actionText = statusTextMap[status] || '更新';
    
    Toast.loading('处理中...', 0, () => {}, true);
    try {
      const response = await updateOrderStatus(orderId, status.toString());
      Toast.hide();
      if (response && response.data && response.data.code === 200) {
        // 更新状态成功，刷新数据
        Toast.success(`${actionText}成功`);
        fetchOrderRecordData(1, false);
      } else {
        console.error('更新订单状态失败', response);
        Toast.fail('更新失败: ' + (response?.data?.errorMsg || '未知错误'), 2);
      }
    } catch (error) {
      console.error('更新订单状态出错', error);
      Toast.hide();
      Toast.fail('更新状态出错，请稍后重试', 2);
    }
  };

  // 切换标签页
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // 重置数据和分页
    if (tab === 'hall') {
      setOrderHallData([]);
    } else {
      setOrderRecordData([]);
    }
    
    setPagination({
      ...pagination,
      pageNo: 1
    });
    
    // 加载对应数据
    if (tab === 'hall') {
      fetchOrderHallData(1, false);
    } else {
      fetchOrderRecordData(1, false);
    }
  };

  // 处理加载更多
  const handleLoadMore = (pageNo) => {
    if (activeTab === 'hall') {
      fetchOrderHallData(pageNo, true);
    } else {
      fetchOrderRecordData(pageNo, true);
    }
  };

  // 初始加载数据
  useEffect(() => {
    if (activeTab === 'hall') {
      fetchOrderHallData(1, false);
    } else {
      fetchOrderRecordData(1, false);
    }
  }, [activeTab]);
  
  // ListView 需要设置的配置
  const getListViewHeight = () => {
    return document.documentElement.clientHeight - 45; // 扣除标签页导航的高度
  };

  // 渲染内容
  const renderContent = () => {
    if (activeTab === 'hall') {
      return (
        <OrderHall
          orders={orderHallData}
          loading={loading}
          onRefresh={() => fetchOrderHallData(1, false)}
          onAccept={handleGrabOrder}
          pagination={pagination}
          onLoadMore={handleLoadMore}
        />
      );
    } else {
      return (
        <OrderRecord
          orders={orderRecordData}
          loading={loading}
          onRefresh={() => fetchOrderRecordData(1, false)}
          onUpdateStatus={handleUpdateOrderStatus}
          pagination={pagination}
          onLoadMore={handleLoadMore}
        />
      );
    }
  };

  return (
    <div className="airport-transfer-container">
      <div className="tabs-container">
        <div className="custom-tabs">
          <div className="custom-tabs-nav">
            <div 
              className={`custom-tab ${activeTab === 'hall' ? 'active' : ''}`}
              onClick={() => handleTabChange('hall')}
            >
              抢单大厅
            </div>
            <div 
              className={`custom-tab ${activeTab === 'record' ? 'active' : ''}`}
              onClick={() => handleTabChange('record')}
            >
              接单记录
            </div>
          </div>
          <div className="custom-tabs-content">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AirportTransferList;
